import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// TODO: replace with someting slimmer
// import { debounce } from 'lodash';
import Text from 'components/shared/Text';
import SVG from 'components/icons/SVG';
import { isNullish } from 'helpers/objects';
import styles from './styles.module.scss';

/*
 * Custom Select for non-mobile
 */

const OPTION_HEIGHT = 38;

const CustomSelect = props => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownReady, setDropdownReady] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState(-1);
  const [optionsAsArray, setOptionsAsArray] = useState([]);
  const [filteredOptionsAsArray, setFilteredOptionsAsArray] = useState([]);
  const [selectPosition, setSelectPosition] = useState({ top: 0, left: 0, width: 0 });
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const selectInputRef = useRef(null);
  const selectButtonRef = useRef(null);
  const optionsContainerRef = useRef(null);

  // Convert options to array with fields. Makes thing easier to have both formats available
  useEffect(() => {
    const array = Object.keys(props.options).map(value => {
      const label = `${props.options[value].label ?? props.options[value]}`;
      return {
        value,
        label,
        index: label.toLowerCase(),
        disabled: !!props.options[value].disabled ?? undefined,
        icon: props.options[value].icon ?? undefined,
        selectedIcon: props.options[value].selectedIcon ?? undefined
      };
    });
    setOptionsAsArray(array);
    setFilteredOptionsAsArray(array);
  }, [props.value, props.options]);

  // Find select box position in order to correctly position the dropdown
  const positionDropdown = useCallback(() => {
    if (!dropdownOpen || !selectInputRef.current) return;
    const rect = selectInputRef.current.getBoundingClientRect();
    let height = `${filteredOptionsAsArray.length * OPTION_HEIGHT}px`;
    if (window.innerHeight - rect.bottom - filteredOptionsAsArray.length * OPTION_HEIGHT < 0) {
      height = `${window.innerHeight - rect.bottom - 25}px`;
    }
    setSelectPosition({
      top: `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.right - rect.left}px`,
      height
    });
  }, [dropdownOpen, filteredOptionsAsArray, selectInputRef]);

  // Run the function when the those props above change
  useEffect(() => {
    positionDropdown();
  }, [positionDropdown]);

  // When opening the dropdown, set listeners to close it when the window is scrolled or resized
  // and whenever the user clicks outside
  // also scroll to the currently selected option
  useEffect(
    () => {
      const onActivity = e => {
        if (dropdownOpen) {
          if (!(e.target instanceof Node)) {
            toggleDropdown();
          } else if (!wrapperRef.current?.contains(e.target)) {
            toggleDropdown();
          }
        }
      };

      if (dropdownOpen) {
        window.addEventListener('mousedown', onActivity);
        window.addEventListener('resize', onActivity, { passive: true, capture: true });
        window.addEventListener('scroll', onActivity, { passive: true, capture: true });

        // Find position of selected option, scroll to it
        if (isNullish(props.value) || props.value === '') setDropdownReady(true);
        else {
          const selectedIndex = optionsAsArray.findIndex(option => option.value === props.value);
          if (selectedIndex === -1) setDropdownReady(true);
          const offset = OPTION_HEIGHT * selectedIndex;
          optionsContainerRef.current.scrollTop = offset;
          if (optionsContainerRef.current.scrollTop === offset) {
            setHighlightedOption(selectedIndex);
            setDropdownReady(true);
          }
          // Not sure why, but the first time the dropdown is opened the scrollTop doesn't "take"
          else {
            setHighlightedOption(selectedIndex);
            setTimeout(() => {
              optionsContainerRef.current.scrollTop = offset;
              setDropdownReady(true);
            });
          }
        }
      }

      return () => {
        window.removeEventListener('mousedown', onActivity);
        window.removeEventListener('resize', onActivity);
        window.removeEventListener('scroll', onActivity);
      };
    },
    [dropdownOpen] // eslint-disable-line
  );

  // Listeners for keyboard navigation
  // Allows user to scroll through options with arrow keys, select with enter/tab, close with esc
  useEffect(
    () => {
      function onKeyUp(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          toggleDropdown();
        }
      }

      function onKeyDown(e) {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          if (highlightedOption > 0) {
            let index = highlightedOption - 1;
            while (filteredOptionsAsArray[index].disabled) index -= 1;
            setHighlightedOption(index);
          }
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          if (highlightedOption < filteredOptionsAsArray.length - 1) {
            let index = highlightedOption + 1;
            while (filteredOptionsAsArray[index].disabled) index += 1;
            setHighlightedOption(index);
          }
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          if (highlightedOption > -1)
            selectOption(filteredOptionsAsArray[highlightedOption].value, highlightedOption);
          else selectOption(filteredOptionsAsArray[0].value, 0);
          selectButtonRef.current.focus();
        }
      }

      const thisSelectInputRef = selectInputRef.current;

      if (dropdownOpen) {
        thisSelectInputRef.addEventListener('keydown', onKeyDown);
        thisSelectInputRef.addEventListener('keyup', onKeyUp);
      }
      return () => {
        thisSelectInputRef.removeEventListener('keydown', onKeyDown);
        thisSelectInputRef.removeEventListener('keyup', onKeyUp);
      };
    },
    [dropdownOpen, filteredOptionsAsArray, highlightedOption] // eslint-disable-line
  );

  // Listeners for keyboard search
  // Allows user to type first few letters of an option to find it, like the native select field
  const handleSearch = e => {
    const getMatch = value => {
      setHighlightedOption(-1);
      const filteredOptions = optionsAsArray.filter(option => option.index.includes(value.toLowerCase()));
      setFilteredOptionsAsArray(filteredOptions);
    };
    getMatch(e.target.value);
    setSearch(e.target.value);
  };

  const toggleDropdown = () => {
    if (!dropdownOpen) {
      setDropdownOpen(true);
      setSearch('');
      setFilteredOptionsAsArray(optionsAsArray);
      setTimeout(() => {
        selectInputRef.current.focus();
      });
    } else {
      setDropdownOpen(false);
      setTimeout(() => {
        setDropdownReady(false);
      });
    }
  };

  // Simulate change event
  const selectOption = (value, index) => {
    toggleDropdown();
    setHighlightedOption(index);
    props.onChange({
      target: {
        id: props.id,
        name: props.name,
        value
      }
    });
  };

  // Show selected value if it exists
  // else show placeholder if it exists
  const getValue = asString => {
    if (props.value) {
      const option = props.options[props.value];
      if (asString) return option?.label ?? option;
      return (
        <>
          {renderIcon(option, true)}
          <Text key={props.value} styles={[styles.optionText]}>
            {option?.label ?? option}
          </Text>
        </>
      );
    }
    if (props.placeholder) {
      if (asString) return props.placeholder;
      return <Text muted>{props.placeholder}</Text>;
    }
    return null;
  };

  const getValueAsString = () => getValue(true);

  const renderIcon = (option, selected) => {
    if (!option?.icon) return null;
    return (
      <div className={styles.optionIconContainer}>
        {option.icon?.asset ? (
          <nbx-asset-icon asset={option.icon.asset} muted={!selected} />
        ) : (
          <SVG
            icon={selected && !isNullish(option.selectedIcon) ? option.selectedIcon : option.icon}
            styles={[styles.optionIcon]}
          />
        )}
      </div>
    );
  };

  return (
    <div className={classnames([styles.selectWrapper, ...props.wrapperStyles])} ref={wrapperRef}>
      {/* input & label for accessibility purposes */}
      <Text
        styles={[
          ...props.labelStyles,
          styles.selectLabel,
          { [styles.selectedOption]: props.value && !dropdownOpen }
        ]}
        tag="label"
        htmlFor={props.id}>
        {props.name}
        {props.labelInlineElements ?? null}
      </Text>
      <input
        autoComplete="off"
        id={props.id}
        name={props.name}
        className={classnames([
          styles.select,
          styles.selectInput,
          {
            [styles.selectOpen]: dropdownReady,
            [styles.hiddenSelect]: !dropdownOpen
          },
          ...props.inputStyles
        ])}
        onClick={toggleDropdown}
        disabled={props.disabled}
        ref={selectInputRef}
        value={search}
        placeholder={getValueAsString()}
        onChange={handleSearch}
      />
      <button
        className={classnames([
          styles.select,
          {
            [styles.selectOpen]: dropdownReady,
            [styles.hiddenSelect]: dropdownOpen,
            [styles.selectError]: props.error
          },
          ...props.inputStyles
        ])}
        onClick={toggleDropdown}
        disabled={props.disabled}
        type="button"
        ref={selectButtonRef}>
        <div className={styles.optionContent}>{getValue()}</div>
      </button>
      <SVG icon="arrowdown" styles={[styles.chevron, { [styles.chevronFlipped]: dropdownOpen }]} />

      {dropdownOpen && (
        <>
          {/* instructions for screen readers, cribbed from react-select */}
          <span aria-live="polite" className={styles.hiddenLabel}>
            <p id="aria-selection-event" />
            <p id="aria-account">
              {highlightedOption > -1
                ? `option ${
                    optionsAsArray[highlightedOption].label ?? optionsAsArray[highlightedOption]
                  } focused, ${highlightedOption + 1} of ${optionsAsArray.length}. ${
                    optionsAsArray.length
                  } options available. Use Up and Down to choose options, press Escape to exit the menu, press Enter or Tab to select the currently focused option and exit the menu.`
                : `no option focused, ${optionsAsArray.length} options available. Use Up and Down to choose options, press Escape to exit the menu, press Enter or Tab to select the currently focused option and exit the menu.`}
            </p>
          </span>
          <div
            className={classnames([
              styles.dropdown,
              { [styles.hidden]: !dropdownReady },
              ...props.dropdownStyles
            ])}
            style={{
              top: selectPosition.top,
              left: selectPosition.left,
              width: selectPosition.width
            }}>
            <div
              className={styles.dropdownScrollContainer}
              style={{
                height: selectPosition.height
              }}
              ref={optionsContainerRef}>
              {filteredOptionsAsArray.map((option, index) => {
                const active = highlightedOption === index;
                return (
                  <button
                    className={classnames([
                      styles.option,
                      { [styles.highlighted]: active },
                      ...props.optionStyles
                    ])}
                    onClick={() => {
                      if (option.disabled) return;
                      selectOption(option.value, index);
                    }}
                    disabled={option.disabled ?? undefined}
                    type="button"
                    tabIndex="-1"
                    key={option.value}
                    title={option.label}>
                    <div className={styles.optionContent}>
                      {renderIcon(option, active)}
                      <Text styles={[styles.optionText]}>{option.label}</Text>
                    </div>
                  </button>
                );
              })}
              {filteredOptionsAsArray.length === 0 && (
                <div
                  className={classnames([styles.option, styles.disabled, ...props.optionStyles])}
                  title="--">
                  <div className={styles.optionContent}>
                    <Text styles={[styles.optionText]}>--</Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {props.error && !props.noErrorLabel && (
        <Text tag="p" styles={[styles.error]}>
          {props.error}
        </Text>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string, // eslint-disable-line
  disabled: PropTypes.bool,
  error: PropTypes.string,
  labelInlineElements: PropTypes.element,
  noErrorLabel: PropTypes.bool,
  wrapperStyles: PropTypes.array,
  labelStyles: PropTypes.array,
  inputStyles: PropTypes.array,
  dropdownStyles: PropTypes.array,
  optionStyles: PropTypes.array
};

CustomSelect.defaultProps = {
  placeholder: undefined,
  value: undefined,
  disabled: false,
  error: undefined,
  labelInlineElements: undefined,
  noErrorLabel: false,
  wrapperStyles: [],
  labelStyles: [],
  inputStyles: [],
  dropdownStyles: [],
  optionStyles: []
};

export default CustomSelect;
